import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../store'
import { SliceType, setCompanyFields, setProductType } from '../../../store/businessSlice'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import styled from 'styled-components'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import TextArea from '../../../components/v2/TextArea'
import Dropdown from '../../../components/v2/Dropdown'
import Input from '../../../components/v2/Input'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import ProgressBar from '../../../components/v2/ProgressBar'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'

const CarCompanyInfo = () => {
  const navigate = useNavigate()
  //
  const { t } = useTranslation()
  //
  const dispatch = useAppDispatch()
  //
  const { companyInfo } = useAppSelector((data: { business: SliceType }) => data.business)
  //

  // DATA
  const pageData = [
    { id: 1, title: `${t('companyInfo')}`, url: `/car-company-info`, active: true, passed: true },
    { id: 2, title: `${t('contactPersonInfo')}`, url: '/company-contact-info', active: false, passed: false },
  ]
  //
  const headerCount = {
    passedCount: 1,
    totalCount: 2,
  }
  //
  // Dropdown Arrays //
  //
  const insurancePurpose = [
    {
      id: '1',
      label: `${t('private')}`,
      value: `${t('private')}`,
    },
    {
      id: '2',
      label: `${t('corporate')}`,
      value: `${t('corporate')}`,
    },
  ]
  //
  const currency = [
    {
      id: '1',
      label: 'GEL',
      value: 'GEL',
    },
    {
      id: '2',
      label: 'USD',
      value: 'USD',
    },
    {
      id: '3',
      label: 'EUR',
      value: 'EUR',
    },
  ]
  //

  // VALIDATION

  //
  // english and georgian letter without space and number regex
  const letterRegex = /^[A-Za-z]+$|^[\u10D0-\u10FF]*$/
  // english and georgian letter with space and number regex
  const letterNumberRegex = /^[ა-ჰa-zA-Z0-9\s]*$/
  // english and georgian letter with space regex
  const letterSpaceRegex = /^[ა-ჰa-zA-Z\s]*$/
  // space regex
  const spaceRegex = /^\S*$/
  //

  const CarInfoValidationSchema = Yup.object({
    companyName: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterNumberRegex, `${t('enterGeorgianOrEnglishLetters')}`)
      .required(`${t('required')}`),
    companyID: Yup.string()
      .min(9, `${t('minNineSymbolos')}`)
      .max(9, `${t('minNineSymbolos')}`)
      .matches(spaceRegex, `${t('spaceValidation')}`)
      .required(`${t('required')}`),
    companyActivity: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterSpaceRegex, `${t('enterGeorgianOrEnglishLetters')}`),
    carBrand: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterRegex, `${t('enterGeorgianOrEnglishLetters')}`),
    carModel: Yup.string()
      .min(2, `${t('minTwoSymbolos')}`)
      .matches(letterNumberRegex, 'mxolod eng and ge'),
    manufactoreYear: Yup.string()
      .min(4, `${t('minFourSymbolos')}`)
      .max(4, `${t('minFourSymbolos')}`)
      .matches(spaceRegex, 'space ar unda iyos'),
    carPrice: Yup.string().matches(spaceRegex, `${t('spaceValidation')}`),
    comment: Yup.string().min(2, `${t('minTwoSymbolos')}`),
  })

  const initialValues = {
    companyName: companyInfo.companyName || '',
    companyID: companyInfo.companyID || '',
    companyActivity: companyInfo.companyActivity || '',
    carBrand: companyInfo.carBrand || '',
    carModel: companyInfo.carModel || '',
    manufactoreYear: companyInfo.manufactoreYear || '',
    carPrice: companyInfo.marketValue || '',
    comment: companyInfo.comment || '',
  }
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: CarInfoValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log(values)
    },
  })

  // Button is valid
  const isValid = formik.isValid === true && companyInfo.companyName && companyInfo.companyID ? true : false
  //   Button is valid

  //   set productType for page and scroll to the top when redering
  useEffect(() => {
    dispatch(setProductType('car'))
    window.scrollTo(0, 0)
  }, [dispatch])

  return (
    <Wrapper>
      <ScreenHeader totalCount={headerCount.totalCount} passedCount={headerCount.passedCount} />
      <Content>
        <Title text={t('companyInfo')} />
        <ProgressBar pageObj={pageData} />
        <WhiteBox>
          <Input
            id="companyName"
            label={t('companyName')}
            type="text"
            required={true}
            value={companyInfo.companyName || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'companyName',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('companyName', value),
            ]}
            width={'fullWidth'}
            error={formik.errors.companyName && companyInfo?.companyName ? true : false}
            errorText={formik.errors.companyName}
            onBlur={formik.handleBlur}
          />

          <Input
            id="company-id"
            label={t('companyID')}
            width={'fullWidth'}
            required={true}
            type="number"
            value={companyInfo.companyID || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'companyID',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('companyID', value),
            ]}
            error={formik.errors.companyID && companyInfo?.companyID ? true : false}
            errorText={formik.errors.companyID}
            onBlur={formik.handleBlur}
          />

          <Input
            id="company-field-of-activity"
            label={t('companyActivity')}
            type="text"
            width={'fullWidth'}
            value={companyInfo.companyActivity || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'companyActivity',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('companyActivity', value),
            ]}
            error={formik.errors.companyActivity && companyInfo?.companyActivity ? true : false}
            errorText={formik.errors.companyActivity}
            onBlur={formik.handleBlur}
          />

          <Input
            id="company-car-brand"
            label={t('carBrand')}
            width={'fullWidth'}
            type="text"
            value={companyInfo.carBrand || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'carBrand',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('carBrand', value),
            ]}
            error={formik.errors.carBrand && companyInfo?.carBrand ? true : false}
            errorText={formik.errors.carBrand}
            onBlur={formik.handleBlur}
          />
          <Input
            id="company-car-model"
            label={t('companyCarModel')}
            width={'fullWidth'}
            type="text"
            value={companyInfo.carModel || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'carModel',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('carModel', value),
            ]}
            error={formik.errors.carModel && companyInfo?.carModel ? true : false}
            errorText={formik.errors.carModel}
            onBlur={formik.handleBlur}
          />
          <Input
            id="manufactore-year"
            label={t('manufactoreYear')}
            width={'fullWidth'}
            type="number"
            value={companyInfo.manufactoreYear || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'manufactoreYear',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('manufactoreYear', value),
            ]}
            error={formik.errors.manufactoreYear && companyInfo?.manufactoreYear ? true : false}
            errorText={formik.errors.manufactoreYear}
            onBlur={formik.handleBlur}
          />
          <Dropdown
            title={t('purpose')}
            isSearchable={true}
            options={insurancePurpose}
            value={companyInfo.purpose || ''}
            onChange={(value) =>
              dispatch(
                setCompanyFields({
                  fieldName: 'purpose',
                  fieldValue: value.value,
                }),
              )
            }
          />
          <Dropdown
            title={t('currency')}
            isSearchable={true}
            options={currency}
            value={companyInfo.currency || ''}
            onChange={(value) =>
              dispatch(
                setCompanyFields({
                  fieldName: 'currency',
                  fieldValue: value.value,
                }),
              )
            }
          />
          <Input
            id="company-car-price"
            label={t('marketValue')}
            width={'fullWidth'}
            type="number"
            value={companyInfo.marketValue || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'marketValue',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('carPrice', value),
            ]}
            error={formik.errors.carPrice && companyInfo?.marketValue ? true : false}
            errorText={formik.errors.carPrice}
            onBlur={formik.handleBlur}
          />

          <Line />

          <TextArea
            id="add-comment"
            label={t('commentAdd')}
            width={'fullWidth'}
            value={companyInfo.comment || ''}
            onChange={(value) => [
              dispatch(
                setCompanyFields({
                  fieldName: 'comment',
                  fieldValue: value,
                }),
              ),
              formik.setFieldValue('comment', value),
            ]}
            error={formik.errors.comment && companyInfo?.comment ? true : false}
            errorText={formik.errors.comment}
            onBlur={formik.handleBlur}
          />
        </WhiteBox>

        <ButtonBox>
          <NavigationButton buttonType="prev" text={t('back')} onClick={() => navigate('/business-category')} />
          <NavigationButton
            buttonType="next"
            text={t('next')}
            onClick={() => navigate('/company-contact-info')}
            disabled={!isValid}
          />
        </ButtonBox>
      </Content>

      <BackgroundIcon/>
    </Wrapper>
  )
}

export default CarCompanyInfo

const Wrapper = styled.div`
  min-height: 100vh;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  /*  */
`

const Content = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  position: relative;
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
  
`

const WhiteBox = styled.div`
  margin-top: 32px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: ${Colors.white};
  max-width: 464px;
  width: 100%;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  padding: 32px 48px;
  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker-popper {
    position: unset !important;
    transform: unset !important;
    margin-top: 10px;
  }
  .react-datepicker {
    display: flex;
    justify-content: center;
  }
  ${Responsive.mobile} {
    padding: 24px 20px;
    margin-top: 24px;
    max-width: 350px;
    width: 100%;
  }
`
const ButtonBox = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 40px;
  button{
    width: 174px;
  }

  ${Responsive.mobile}{
    button{
      width: 140px;
    }
  }
`

const Line = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${Colors.secondaryGrey};
`
